import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Upload, Loader2, Pen, PenLine, ImageIcon } from 'lucide-react';
// Loader2 still used by generateSignature button
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { API_BASE_URL } from '../../config/api';
import { handleUnauthorized } from '../../services/api';
import { toast } from '../../components/ui/use-toast';

interface SignatureSectionProps {
  userId: number;
  firstName: string;
  lastName: string;
  signaturePath?: string;
  onSignatureUpdated: (path: string) => void;
}

const SignatureSection: React.FC<SignatureSectionProps> = ({
  userId,
  firstName,
  lastName,
  signaturePath,
  onSignatureUpdated,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('profile');
  const [generating, setGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const loadExistingSignature = useCallback(async () => {
    if (!signaturePath) {
      setPreviewUrl(null);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/users/${userId}/signature`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        setPreviewUrl(URL.createObjectURL(blob));
      } else if (res.status === 401) {
        handleUnauthorized(); return;
      }
    } catch {
      console.error('Failed to load signature');
    }
  }, [signaturePath, userId]);

  useEffect(() => {
    loadExistingSignature();
  }, [loadExistingSignature]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast({ title: 'Invalid file type', description: 'Please upload a JPG, PNG, or GIF image.', variant: 'destructive' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum file size is 2MB.', variant: 'destructive' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Navigate to signature editor with the selected file
    if (fileInputRef.current) fileInputRef.current.value = '';
    navigate('/signature-editor', { state: { file } });
  };

  const generateSignature = async () => {
    const fullName = `${firstName} ${lastName}`.trim();
    if (!fullName) {
      toast({ title: 'Missing name', description: 'First and last name are required to generate a signature.', variant: 'destructive' });
      return;
    }

    setGenerating(true);
    try {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Clear canvas
      canvas.width = 400;
      canvas.height = 150;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw cursive-style signature on transparent background
      ctx.fillStyle = '#1a1a2e';
      ctx.font = 'italic 48px "Brush Script MT", "Segoe Script", "Comic Sans MS", cursive';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(fullName, canvas.width / 2, canvas.height / 2);

      // Add a subtle underline
      const textMetrics = ctx.measureText(fullName);
      const startX = (canvas.width - textMetrics.width) / 2 - 10;
      const endX = (canvas.width + textMetrics.width) / 2 + 10;
      ctx.beginPath();
      ctx.moveTo(startX, canvas.height / 2 + 30);
      ctx.bezierCurveTo(
        startX + (endX - startX) * 0.3, canvas.height / 2 + 35,
        startX + (endX - startX) * 0.7, canvas.height / 2 + 25,
        endX, canvas.height / 2 + 30
      );
      ctx.strokeStyle = '#1a1a2e';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Convert to blob and upload
      canvas.toBlob(async (blob) => {
        if (!blob) return;

        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('signature', blob, 'signature.png');

        const res = await fetch(`${API_BASE_URL}/profile/signature`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        const data = await res.json();
        if (!res.ok) {
          if (res.status === 401) { handleUnauthorized(); return; }
          throw new Error(data.message || 'Upload failed');
        }

        onSignatureUpdated(data.signature_path);
        setPreviewUrl(canvas.toDataURL('image/png'));
        toast({ title: 'Signature generated', description: 'Your auto-generated signature has been saved.', variant: 'success' });
        setGenerating(false);
      }, 'image/png');
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to generate signature', variant: 'destructive' });
      setGenerating(false);
    }
  };

  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="bg-slate-50 border-b">
        <CardTitle className="flex items-center gap-2 text-foreground">
          <PenLine className="h-5 w-5" /> {t('signature')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 pt-6">
        {/* Current signature preview */}
        <div>
          <p className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-1.5">
            <ImageIcon className="h-3.5 w-3.5 text-slate-400" /> {t('current_signature')}
          </p>
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 bg-slate-50/30 flex items-center justify-center min-h-[120px]">
            {previewUrl ? (
              <img src={previewUrl} alt="Current signature" className="max-h-[100px] object-contain" />
            ) : (
              <div className="text-center">
                <PenLine className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">{t('no_signature')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Upload signature */}
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif"
            className="hidden"
            onChange={handleFileUpload}
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-4 w-4 mr-2" />
            {t('upload_signature_image')}
          </Button>

          <Button
            onClick={generateSignature}
            disabled={generating}
          >
            {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Pen className="h-4 w-4 mr-2" />}
            {t('auto_generate_from_name')}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          Accepted formats: JPG, PNG, GIF. Max size: 2MB.
        </p>

        {/* Hidden canvas for signature generation */}
        <canvas ref={canvasRef} className="hidden" />
      </CardContent>
    </Card>
  );
};

export default SignatureSection;
